import { useCallback, useEffect, useState } from 'react';

import { ApiError } from './api/client';

/**
 * Loading a thing from the API.
 *
 * Three states, always: loading, error, data. A hook that returned only data would leave
 * each screen to invent its own handling, and the screens that forgot would render an
 * empty table for a failed request — indistinguishable from a successful empty one.
 */
export function useResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[] = [],
): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    load()
      .then((result) => {
        // A response arriving after the screen moved on must not write to dead state.
        if (!cancelled) setData(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(messageOf(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Dependencies are supplied by the caller by design: what a screen re-fetches on is a
    // decision only that screen can make. `nonce` is what `reload()` increments.
  }, [...deps, nonce]);

  return { data, error, loading, reload: useCallback(() => setNonce((value) => value + 1), []) };
}

/**
 * Running an action that changes something.
 *
 * Returns the error rather than throwing it, because every caller has to show it and a
 * thrown error in an event handler is one nobody sees.
 */
export function useAction(): {
  run: (work: () => Promise<void>) => Promise<boolean>;
  error: string | null;
  busy: boolean;
  clearError: () => void;
} {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (work: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (cause: unknown) {
      setError(messageOf(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { run, error, busy, clearError: useCallback(() => setError(null), []) };
}

function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'Something went wrong.';
}
